/**
 * tree-node — Zod mirror of the canonical Pydantic schema.
 *
 * Authoritative source: `packages/market-eval/src/market_eval/schema/`
 * (`time_ref.py` + `tree_node.py`). This file MUST stay in sync — drift is
 * caught by the unit test next to this file.
 */
import { z } from 'zod'

// ─── Enums ──────────────────────────────────────────────────────────────

export const BarResolutionSchema = z.enum(['1m', '5m', '15m', '1h', '1d'])
export type BarResolution = z.infer<typeof BarResolutionSchema>

export const SessionKindSchema = z.enum(['premarket', 'regular', 'afterhours'])
export type SessionKind = z.infer<typeof SessionKindSchema>

export const SessionBoundarySchema = z.enum(['start', 'end'])
export type SessionBoundary = z.infer<typeof SessionBoundarySchema>

export const PriceFieldSchema = z.enum(['open', 'high', 'low', 'close', 'volume'])
export type PriceField = z.infer<typeof PriceFieldSchema>

export const AggFnSchema = z.enum(['max', 'min', 'first', 'last', 'mean', 'sum'])
export type AggFn = z.infer<typeof AggFnSchema>

export const ArithOpSchema = z.enum(['add', 'subtract', 'multiply', 'divide', 'percent_change'])
export type ArithOp = z.infer<typeof ArithOpSchema>

export const IndicatorFnSchema = z.enum(['sma', 'ema', 'vwap', 'rsi'])
export type IndicatorFn = z.infer<typeof IndicatorFnSchema>

export const IndicatorFieldSchema = z.enum(['open', 'high', 'low', 'close', 'typical'])
export type IndicatorField = z.infer<typeof IndicatorFieldSchema>

export const CompareOpSchema = z.enum(['gt', 'gte', 'lt', 'lte', 'eq'])
export type CompareOp = z.infer<typeof CompareOpSchema>

// ─── DayRef ─────────────────────────────────────────────────────────────

export const OffsetDayRefSchema = z
  .object({
    kind: z.literal('offset'),
    trading_days_ago: z.number().int(),
  })
  .strict()
export type OffsetDayRef = z.infer<typeof OffsetDayRefSchema>

export const DateDayRefSchema = z
  .object({
    kind: z.literal('date'),
    date: z.string(),
  })
  .strict()
export type DateDayRef = z.infer<typeof DateDayRefSchema>

export const DayRefSchema = z.discriminatedUnion('kind', [OffsetDayRefSchema, DateDayRefSchema])
export type DayRef = z.infer<typeof DayRefSchema>

// ─── TimeOfDay ──────────────────────────────────────────────────────────

export const SessionTimeOfDaySchema = z
  .object({
    kind: z.literal('session'),
    session: SessionKindSchema,
    boundary: SessionBoundarySchema,
    offset_minutes: z.number().int(),
  })
  .strict()
export type SessionTimeOfDay = z.infer<typeof SessionTimeOfDaySchema>

export const ClockTimeOfDaySchema = z
  .object({
    kind: z.literal('clock'),
    time_et: z.string(),
  })
  .strict()
export type ClockTimeOfDay = z.infer<typeof ClockTimeOfDaySchema>

export const TimeOfDaySchema = z.discriminatedUnion('kind', [
  SessionTimeOfDaySchema,
  ClockTimeOfDaySchema,
])
export type TimeOfDay = z.infer<typeof TimeOfDaySchema>

// ─── EvalPoint / EvalRange / BarQuery ────────────────────────────────────

export const EvalPointSchema = z
  .object({
    day: DayRefSchema,
    at: TimeOfDaySchema,
  })
  .strict()
export type EvalPoint = z.infer<typeof EvalPointSchema>

export const EvalRangeSchema = z
  .object({
    kind: z.literal('range'),
    start_day: DayRefSchema,
    start_at: TimeOfDaySchema,
    end_day: DayRefSchema,
    end_at: TimeOfDaySchema,
  })
  .strict()
export type EvalRange = z.infer<typeof EvalRangeSchema>

// Shared session-list rules (mirrors Pydantic `_validate_sessions`):
// non-empty, no duplicates, and 1d resolution must use sessions=['regular'].
const refineSessions = (
  ctx: z.RefinementCtx,
  sessions: SessionKind[],
  resolution: BarResolution,
) => {
  if (sessions.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sessions'],
      message: 'sessions must contain at least one session',
    })
  }
  if (new Set(sessions).size !== sessions.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sessions'],
      message: 'sessions must not contain duplicates',
    })
  }
  if (resolution === '1d' && !(sessions.length === 1 && sessions[0] === 'regular')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sessions'],
      message:
        "1d resolution must use sessions=['regular'] — daily bars are single-session aggregates",
    })
  }
}

export const BarSourceSchema = z
  .object({
    ticker: z.string(),
    resolution: BarResolutionSchema,
    sessions: z.array(SessionKindSchema).default(['regular']),
  })
  .strict()
  .superRefine((val, ctx) => refineSessions(ctx, val.sessions, val.resolution))
export type BarSource = z.infer<typeof BarSourceSchema>

export const BarQuerySchema = z
  .object({
    ticker: z.string(),
    resolution: BarResolutionSchema,
    window: EvalRangeSchema,
    sessions: z.array(SessionKindSchema).default(['regular']),
  })
  .strict()
  .superRefine((val, ctx) => refineSessions(ctx, val.sessions, val.resolution))
export type BarQuery = z.infer<typeof BarQuerySchema>

// ─── NumericNode leaves ────────────────────────────────────────────────

export const ConstantNodeSchema = z
  .object({
    kind: z.literal('constant'),
    // Pydantic's `float | int` with strict=True rejects bool — Zod's z.number()
    // already excludes bool at the type-check level (true/false aren't number).
    value: z.number(),
  })
  .strict()
export type ConstantNode = z.infer<typeof ConstantNodeSchema>

export const QuoteNodeSchema = z
  .object({
    kind: z.literal('quote'),
    ticker: z.string(),
    field: PriceFieldSchema,
    time: EvalPointSchema,
  })
  .strict()
export type QuoteNode = z.infer<typeof QuoteNodeSchema>

export const AggregateNodeSchema = z
  .object({
    kind: z.literal('aggregate'),
    fn: AggFnSchema,
    field: PriceFieldSchema,
    source: BarQuerySchema,
  })
  .strict()
export type AggregateNode = z.infer<typeof AggregateNodeSchema>

export const IndicatorNodeSchema = z
  .object({
    kind: z.literal('indicator'),
    fn: IndicatorFnSchema,
    field: IndicatorFieldSchema,
    period: z.number().int().min(1).nullable(),
    source: BarSourceSchema,
    at: EvalPointSchema,
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.fn === 'vwap') {
      if (val.period !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['period'],
          message: 'vwap takes no `period` (must be null)',
        })
      }
    } else {
      if (val.period === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['period'],
          message: `${val.fn} requires \`period\` (int >= 1)`,
        })
      }
    }
  })
export type IndicatorNode = z.infer<typeof IndicatorNodeSchema>

// NumericNode is recursive (ArithmeticNode left/right are NumericNode).
// Same z.lazy + hand-rolled type alias pattern as the legacy mirror.

export type NumericNode =
  | ConstantNode
  | QuoteNode
  | AggregateNode
  | IndicatorNode
  | { kind: 'arithmetic'; operator: ArithOp; left: NumericNode; right: NumericNode }

export const ArithmeticNodeSchema: z.ZodType<
  { kind: 'arithmetic'; operator: ArithOp; left: NumericNode; right: NumericNode },
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
  z
    .object({
      kind: z.literal('arithmetic'),
      operator: ArithOpSchema,
      left: NumericNodeSchema,
      right: NumericNodeSchema,
    })
    .strict(),
)

// Input slot is `unknown` because BarSource/BarQuery use `.default(['regular'])`
// on `sessions`, which makes the schema's input type diverge from the output
// type — Zod's recursive `z.ZodType<T>` shorthand defaults Input=Output and
// would reject the assignment otherwise.
export const NumericNodeSchema: z.ZodType<NumericNode, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.union([
    ConstantNodeSchema,
    QuoteNodeSchema,
    AggregateNodeSchema,
    IndicatorNodeSchema,
    ArithmeticNodeSchema,
  ]),
)

// ─── BooleanNode (Compare / And / Or) ──────────────────────────────────

export const CompareNodeSchema = z
  .object({
    kind: z.literal('compare'),
    operator: CompareOpSchema,
    left: NumericNodeSchema,
    right: NumericNodeSchema,
  })
  .strict()
export type CompareNode = z.infer<typeof CompareNodeSchema>

export type BooleanNode =
  | CompareNode
  | { kind: 'and'; children: BooleanNode[] }
  | { kind: 'or'; children: BooleanNode[] }

export const AndGroupSchema: z.ZodType<
  { kind: 'and'; children: BooleanNode[] },
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
  z
    .object({
      kind: z.literal('and'),
      children: z.array(BooleanNodeSchema),
    })
    .strict(),
)

export const OrGroupSchema: z.ZodType<
  { kind: 'or'; children: BooleanNode[] },
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
  z
    .object({
      kind: z.literal('or'),
      children: z.array(BooleanNodeSchema),
    })
    .strict(),
)

export const BooleanNodeSchema: z.ZodType<BooleanNode, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.union([CompareNodeSchema, AndGroupSchema, OrGroupSchema]),
)

// ─── TreeNode flat union (LLM authoring surface) ───────────────────────

export type TreeNode = NumericNode | BooleanNode

export const TreeNodeSchema: z.ZodType<TreeNode, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.union([NumericNodeSchema, BooleanNodeSchema]),
)
